import { graphql } from 'react-relay';
import React from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { useViewPageConversationData } from '../ViewPageConversationData.tsx';

import type {
	ObjectAddCommentProfileWrapperUserQuery as ObjectAddCommentProfileWrapperUserQuery$data,
	ObjectAddCommentProfileWrapperUserQueryVariables as ObjectAddCommentProfileWrapperUserQuery$variables,
} from './__types__/ObjectAddCommentProfileWrapperUserQuery.ts';
const ObjectAddCommentProfileWrapperUserQuery = graphql`
	query ObjectAddCommentProfileWrapperUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

const styles = cssMap({
	addCommentWrapperStyles: {
		width: '100%',
	},
	addCommentWrapperWithBannerStyles: {
		display: 'grid',
		gridTemplateColumns: '32px minmax(0, 1fr)',
		columnGap: token('space.200'),
		width: '100%',
	},
	addCommentOpenContainerStyles: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		gap: token('space.200'),
		alignItems: 'flex-start',
	},
	addCommentOpenContainerWithBannerStyles: {
		display: 'contents',
	},
	addCommentEditorStyles: {
		gridColumn: '2',
		gridRow: '2',
		width: '100%',
		minWidth: 0,
	},
	bannerStyles: {
		gridColumn: '2',
		gridRow: '1',
		minWidth: 0,
	},
	avatarStyles: {
		gridColumn: '1',
		gridRow: '2',
		width: '32px',
		height: '32px',
		flexShrink: 0,
	},
});

const avatarFloatStyle = { float: 'left' } as const;

export interface ObjectAddCommentProfileWrapperProps {
	children: React.ReactNode;
	banner?: React.ReactNode;
}

export const ObjectAddCommentProfileWrapper = ({
	children,
	banner,
}: ObjectAddCommentProfileWrapperProps): React.JSX.Element => {
	const { userId } = useSessionData();
	const renderServerPlaceholder = useIsSSROrHydrating();
	const { currentUser } = useViewPageConversationData();

	const { data: userData, loading } = useQueryInterop<
		ObjectAddCommentProfileWrapperUserQuery$data,
		ObjectAddCommentProfileWrapperUserQuery$variables
	>(ObjectAddCommentProfileWrapperUserQuery, {
		variables: { id: userId || '' },
		fetchPolicy: fg('confluence-comments-fix-avatar-flash') ? 'cache-first' : 'cache-and-network',
		// Skip the query if data is provided or the user is anonymous
		skip: !userId || currentUser !== undefined,
	});
	const canViewProfile = true;
	const user = currentUser === undefined ? userData?.user : currentUser;
	const primaryPhoto = currentUser?.photos?.find((photo) => photo?.isPrimary) ?? user?.photos?.[0];
	const userInfo = {
		avatarUrl: primaryPhoto?.value || '',
		accountId: currentUser?.id ?? userId,
		displayName: user?.displayName || '',
	};

	return (
		<Box xcss={banner ? styles.addCommentWrapperWithBannerStyles : styles.addCommentWrapperStyles}>
			{banner ? <Box xcss={styles.bannerStyles}>{banner}</Box> : null}
			<Box
				xcss={
					banner
						? styles.addCommentOpenContainerWithBannerStyles
						: styles.addCommentOpenContainerStyles
				}
			>
				<Box
					xcss={styles.avatarStyles}
					// float left not possible in map
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={avatarFloatStyle}
				>
					{(currentUser === undefined && loading) || renderServerPlaceholder ? null : (
						<ProfileAvatar
							canViewProfile={canViewProfile}
							src={userInfo.avatarUrl}
							userId={userInfo.accountId}
							isAnonymous={!userInfo.accountId}
							name={userInfo.displayName}
							testId="profile-avatar-comment"
						/>
					)}
				</Box>
				<Box xcss={styles.addCommentEditorStyles}>{children}</Box>
			</Box>
		</Box>
	);
};

export interface CommentAuthorInfo {
	avatarUrl: string;
	accountId: string | null | undefined;
	displayName: string;
	/** True while the `currentUser` fallback query is in flight; avoid rendering an avatar `src`. */
	loading: boolean;
	/** Set if the `currentUser` fallback query failed; avoid rendering an avatar `src`. */
	error?: Error;
}

/**
 * Resolves the current user's avatar/name for the footer's collapsed add-comment trigger,
 * preferring `ViewPageConversationDataContext` over a GraphQL fallback. Duplicates this
 * component's own resolution above rather than sharing it, so master's render path here is
 * untouched.
 */
export const useCommentAuthorInfo = (): CommentAuthorInfo => {
	const { userId } = useSessionData();
	const { currentUser } = useViewPageConversationData();

	const {
		data: userData,
		loading,
		error,
	} = useQueryInterop<
		ObjectAddCommentProfileWrapperUserQuery$data,
		ObjectAddCommentProfileWrapperUserQuery$variables
	>(ObjectAddCommentProfileWrapperUserQuery, {
		variables: { id: userId || '' },
		fetchPolicy: fg('confluence-comments-fix-avatar-flash') ? 'cache-first' : 'cache-and-network',
		// Skip the query if data is provided or the user is anonymous
		skip: !userId || currentUser !== undefined,
	});
	const user = currentUser === undefined ? userData?.user : currentUser;
	const primaryPhoto = currentUser?.photos?.find((photo) => photo?.isPrimary) ?? user?.photos?.[0];

	return {
		avatarUrl: primaryPhoto?.value || '',
		accountId: currentUser?.id ?? userId,
		displayName: user?.displayName || '',
		loading: currentUser === undefined && loading,
		error: currentUser === undefined ? error : undefined,
	};
};
